// Local storage + cookies backed pending/approval store
// Shapes are generic with type-safe helpers per collection

// Note: utils can reference static data to rebuild full payloads for edits
import { organizationsData } from "@/data/organizationsData";
import { store } from "@/src/store";
import { pushFlash } from "@/src/store/features/flashSlice";
import { trackCacheRead, trackCacheWrite, trackMerge } from "@/utils/trafficTracker";
const KEYS = {
  cookiePrefs: "pc_cookie_prefs",
  version: "pc_db_version",
  pending: type => `pc_pending_${type}`,
  newlyAdded: day => `pc_newlyAdded_${day}`,
  rejected: type => `pc_rejected_${type}`,
  userId: "pc_user_id",
  notificationsLastSeen: userId => `pc_notifications_last_seen_${userId}`,
  flashMessage: "pc_flash_message",
  createPrefill: "pc_create_prefill",
  regulationVersions: regId => `pc_reg_versions_${regId}`,
  allowRandomGeneration: "pc_allow_random_generation",
  enableCreateProdcertOrg: "pc_enable_create_prodcert_org",
  enableRandomEventPrefill: "pc_enable_random_event_prefill",
  latestOverlay: type => `pc_latest_overlay_${type}`,
  // Track deactivated organization slugs (collapsed list in Admin)
  deactivatedOrgs: "pc_deactivated_org_slugs",
  // User-saved filter chips (persisted across pages)
  savedFilters: "pc_savedFilters"
};

// Return browser localStorage when available; null on server/denied environments
function getStorage() {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
export const cookieConsent = {
  get() {
    const s = getStorage();
    if (!s) return null;
    const raw = s.getItem(KEYS.cookiePrefs);
    return raw ? JSON.parse(raw) : null;
  },
  set(prefs) {
    const s = getStorage();
    if (!s) return;
    s.setItem(KEYS.cookiePrefs, JSON.stringify(prefs));
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("pc-cookie-consent-changed"));
    }
  },
  allowed() {
    const prefs = this.get();
    return !!prefs?.functional;
  }
};

// Basic helpers
const safeParse = (raw, fallback) => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};
export function addPending(type, item) {
  const s = getStorage();
  if (!s) throw new Error("Local storage unavailable");
  if (!cookieConsent.allowed()) throw new Error("Cookies not allowed");
  const key = KEYS.pending(type);
  const list = safeParse(s.getItem(key), []);
  list.push(item);
  const isQuotaExceeded = e => {
    if (!e) return false;
    const msg = String(e && (e.message || e.toString()) || "").toLowerCase();
    return typeof e.code === "number" && (e.code === 22 || e.code === 1014) || typeof e.name === "string" && e.name.includes("QuotaExceeded") || msg.includes("quota") || msg.includes("exceeded");
  };
  const types = ["organization", "event", "regulation", "article"];
  const pruneOldestPending = () => {
    // Build list of all pending with their type and createdAt
    let oldest = null;
    for (const t of types) {
      const arr = safeParse(s.getItem(KEYS.pending(t)), []);
      for (let i = 0; i < arr.length; i++) {
        const c = Number(arr[i]?.createdAt || 0);
        if (!oldest || c < oldest.createdAt) oldest = {
          type: t,
          idx: i,
          createdAt: c
        };
      }
    }
    if (!oldest) return false;
    const arr = safeParse(s.getItem(KEYS.pending(oldest.type)), []);
    if (oldest.idx < 0 || oldest.idx >= arr.length) return false;
    arr.splice(oldest.idx, 1);
    try {
      s.setItem(KEYS.pending(oldest.type), JSON.stringify(arr));
      return true;
    } catch {
      return false;
    }
  };
  const pruneOldNewlyAddedBuckets = () => {
    // Collect all pc_newlyAdded_ keys and sort by day ascending
    const keys = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i) || "";
      if (k.startsWith("pc_newlyAdded_")) keys.push(k);
    }
    if (keys.length <= 3) return false; // keep at least 3 most recent buckets
    keys.sort(); // YYYY-MM-DD sorting works lexicographically
    const toRemove = keys.slice(0, Math.max(0, keys.length - 3));
    if (!toRemove.length) return false;
    try {
      for (const k of toRemove) s.removeItem(k);
      return true;
    } catch {
      return false;
    }
  };
  const pruneNonCritical = () => {
    let changed = false;
    try {
      s.removeItem(KEYS.flashMessage);
      changed = true;
    } catch {}
    try {
      // Remove create prefill (transient)
      s.removeItem(KEYS.createPrefill);
      changed = true;
    } catch {}
    return changed;
  };
  const value = JSON.stringify(list);
  try {
    s.setItem(key, value);
  } catch (e) {
    if (!isQuotaExceeded(e)) throw e;
    // Try pruning strategies progressively, then retry a few times
    const maxAttempts = 5;
    let attempt = 0;
    while (attempt < maxAttempts) {
      attempt++;
      const pruned = pruneOldestPending() || pruneOldNewlyAddedBuckets() || pruneNonCritical();
      try {
        s.setItem(key, value);
        return;
      } catch (err) {
        if (!isQuotaExceeded(err)) throw err;
        if (!pruned) break;
      }
    }
    throw new Error("Your browser storage is full. Please delete older drafts or clear site data and try again.");
  }
}
export function getPending(type) {
  const s = getStorage();
  if (!s) return [];
  const key = KEYS.pending(type);
  return safeParse(s.getItem(key), []);
}
export function replacePending(type, list) {
  const s = getStorage();
  if (!s) return;
  const key = KEYS.pending(type);
  s.setItem(key, JSON.stringify(list));
}

// Newly Added: bucketed by day YYYY-MM-DD
export function addApproved(item) {
  const s = getStorage();
  if (!s) throw new Error("Local storage unavailable");
  if (!cookieConsent.allowed()) throw new Error("Cookies not allowed");
  const day = new Date(item.approvalTimestamp).toISOString().slice(0, 10);
  const key = KEYS.newlyAdded(day);
  let list = safeParse(s.getItem(key), []);

  // If this is an edit, remove any earlier approved entry for same id/slug across ALL buckets
  if (item.isEdit) {
    try {
      // Gather all bucket keys (heuristic: iterate last 30 days) and remove older versions
      const now = new Date(item.approvalTimestamp);
      for (let i = 0; i < 30; i++) {
        const d = new Date(now.getTime() - i * 86400000).toISOString().slice(0, 10);
        const k = KEYS.newlyAdded(d);
        const bucketList = safeParse(s.getItem(k), []);
        let changed = false;
        const filtered = bucketList.filter(entry => {
          if (entry.type !== item.type) return true;
          const matchId = entry.payload?.id;
          const matchSlug = entry.payload?.slug;
          const newId = item.payload?.id;
          const newSlug = item.payload?.slug;
          if (newId && matchId && String(matchId) === String(newId) || newSlug && matchSlug && String(matchSlug) === String(newSlug)) {
            // skip (remove older)
            changed = true;
            return false;
          }
          return true;
        });
        if (changed) {
          s.setItem(k, JSON.stringify(filtered));
        }
      }
    } catch {
      // best-effort; ignore failures
    }
  }
  list.push(item);
  s.setItem(key, JSON.stringify(list));
  s.setItem(KEYS.version, `${day}:${item.id}`);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("pc-notifications-changed"));
  }
}
export function getNewlyAddedForDay(day) {
  const s = getStorage();
  if (!s) return [];
  return safeParse(s.getItem(KEYS.newlyAdded(day)), []);
}
export function getDbVersion() {
  const s = getStorage();
  return s ? s.getItem(KEYS.version) : null;
}
export function setDbVersion(value) {
  const s = getStorage();
  if (!s) return;
  s.setItem(KEYS.version, value);
}

// ---------------- Deactivated Pages (Organizations) -----------------
// Maintain a simple set of slugs that are considered deactivated (hidden from public lists by default)
function getDeactivatedSet() {
  const s = getStorage();
  if (!s) return new Set();
  const raw = s.getItem(KEYS.deactivatedOrgs);
  const arr = safeParse(raw, []);
  return new Set(arr.map(v => String(v || "").toLowerCase()));
}
function setDeactivatedSet(slugs) {
  const s = getStorage();
  if (!s) return;
  s.setItem(KEYS.deactivatedOrgs, JSON.stringify(Array.from(slugs)));
  if (typeof window !== "undefined") window.dispatchEvent(new Event("pc-deactivated-changed"));
}
export function getAllDeactivatedOrgSlugs() {
  return Array.from(getDeactivatedSet());
}
export function isOrgDeactivated(slugOrId) {
  const key = String(slugOrId || "").toLowerCase();
  const set = getDeactivatedSet();
  return set.has(key);
}
export function setOrgDeactivated(slugOrId, deactivated) {
  const key = String(slugOrId || "").toLowerCase();
  const set = getDeactivatedSet();
  if (deactivated) set.add(key);else set.delete(key);
  setDeactivatedSet(set);
}

// ---------------- Saved Filters (UI chips) -----------------

function normalizeFilterLabel(v) {
  return String(v || "").trim();
}

// ---------------- Region canonicalization & matching -----------------
import filterTagData from "@/data/filterTagData.json";
const regionsData = filterTagData.regions;
export const CANONICAL_REGIONS = ["Global", "North America", "Europe", "Latin America", "Oceana", "Asia", "Middle East", "Africa"];
// Build a lookup: lowercase string → CanonicalRegion (super-region name)
// from filterTagData.json so matching stays in sync with the data file.
const _superRegionMatchMap = (() => {
  const map = new Map();
  const nameToCanon = {
    Global: "Global",
    "North America": "North America",
    Europe: "Europe",
    "Latin America": "Latin America",
    Oceana: "Oceana",
    Asia: "Asia",
    "Middle East": "Middle East",
    Africa: "Africa"
  };
  const idToCanon = {
    GLOBAL: "Global",
    NA: "North America",
    EUROPE: "Europe",
    LATAM: "Latin America",
    OCEANIA: "Oceana",
    ASIA: "Asia",
    ME: "Middle East",
    AFRICA: "Africa"
  };
  for (const sr of regionsData) {
    const canon = idToCanon[sr.id] || nameToCanon[sr.name];
    if (!canon) continue;
    // Map the super-region name and id
    map.set(sr.name.toLowerCase(), canon);
    map.set(sr.id.toLowerCase(), canon);
    // Map each region name, abbrev, and id
    for (const r of sr.regions) {
      map.set(r.name.toLowerCase(), canon);
      if (r.abbrev && r.abbrev !== "-") map.set(r.abbrev.toLowerCase(), canon);
      map.set(r.id.toLowerCase(), canon);
      // Map sub-regions (states/provinces)
      if ("subRegions" in r && Array.isArray(r.subRegions)) {
        for (const sub of r.subRegions) {
          map.set(sub.name.toLowerCase(), canon);
          // Don't map 2-letter state abbrevs — too ambiguous (e.g., "CA" = California vs Canada)
        }
      }
    }
  }
  // Additional common aliases
  map.set("global", "Global");
  map.set("worldwide", "Global");
  map.set("world wide", "Global");
  map.set("international", "Global");
  map.set("u.s", "North America");
  map.set("u.s.", "North America");
  map.set("u.s.a", "North America");
  map.set("united states", "North America");
  map.set("united states of america", "North America");
  map.set("canadian", "North America");
  map.set("south america", "Latin America");
  map.set("central america", "Latin America");
  map.set("caribbean", "Latin America");
  map.set("oceania", "Oceana");
  map.set("pacific", "Oceana");
  map.set("e.u", "Europe");
  map.set("e.u.", "Europe");
  map.set("european union", "Europe");
  // Legacy: "United Kingdom" was flattened into its constituent countries
  // (England/Scotland/Wales/Northern Ireland), so the "UK" region id/abbrev
  // no longer exists in filterTagData.json. Keep these aliases so records
  // tagged with the old value still canonicalize to Europe for matching.
  map.set("uk", "Europe");
  map.set("u.k", "Europe");
  map.set("u.k.", "Europe");
  map.set("united kingdom", "Europe");
  map.set("great britain", "Europe");
  map.set("britain", "Europe");
  map.set("mideast", "Middle East");
  map.set("mena", "Middle East");
  map.set("korea", "Asia");
  map.set("south korea", "Asia");
  return map;
})();

/**
 * Build a Set of all lowercase strings that should match a given super-region.
 * Cached per CanonicalRegion for fast lookups.
 */
const _matchSets = (() => {
  const result = new Map();
  CANONICAL_REGIONS.forEach(canon => {
    result.set(canon, new Set());
  });
  Array.from(_superRegionMatchMap.entries()).forEach(([key, canon]) => {
    result.get(canon).add(key);
  });
  return result;
})();
export function canonicalizeRegion(label) {
  const raw = String(label || "").trim();
  if (!raw) return null;
  const v = raw.toLowerCase();
  // Direct lookup
  const direct = _superRegionMatchMap.get(v);
  if (direct) return direct;
  // Partial / contains checks
  if (v.includes("europe") || v.includes("european union")) return "Europe";
  if (v.includes("asia")) return "Asia";
  if (v.includes("latin")) return "Latin America";
  if (v.includes("middle east")) return "Middle East";
  if (v.includes("africa")) return "Africa";
  return null;
}
export function doesRegionLabelMatchSelection(regionLabel, selected) {
  const v = String(regionLabel || "").trim().toLowerCase();
  if (!v) return false;
  const matchSet = _matchSets.get(selected);
  if (matchSet && matchSet.has(v)) return true;
  // Partial / contains fallback for each super-region
  switch (selected) {
    case "Global":
      return v.includes("worldwide") || v.includes("international");
    case "Europe":
      return v.includes("europe") || v.includes("european union");
    case "Latin America":
      return v.includes("latin");
    case "Asia":
      return v.includes("asia");
    case "Middle East":
      return v.includes("middle east");
    case "Africa":
      return v.includes("africa");
    default:
      return false;
  }
}

/**
 * Check whether a region tag on a card matches any of the user's preferred
 * regions, taking the super-region hierarchy into account.
 *
 * e.g. if the user's pref is "North America", this returns true for "US",
 * "Canada", "USA", "California", etc.
 */
export function doesRegionMatchPrefs(regionLabel, prefRegions) {
  if (prefRegions.length === 0) return false;
  const tagLower = String(regionLabel || "").trim().toLowerCase();
  if (!tagLower) return false;

  // Direct exact match first
  if (prefRegions.some(r => r.toLowerCase() === tagLower)) return true;

  // Canonicalize the tag to its super-region and check prefs
  const tagCanon = canonicalizeRegion(regionLabel);
  if (tagCanon && prefRegions.some(r => r === tagCanon)) return true;

  // Canonicalize each pref and compare with tag's canonical form
  // (handles case where pref is a sub-region matching the same super-region)
  if (tagCanon) {
    for (const pref of prefRegions) {
      const prefCanon = canonicalizeRegion(pref);
      if (prefCanon && prefCanon === tagCanon) return true;
    }
  }

  // Check if the tag is a sub-region of any pref super-region using matchSets
  for (const pref of prefRegions) {
    if (doesRegionLabelMatchSelection(regionLabel, pref)) return true;
  }
  return false;
}

/** Returns a timestamp for end-of-day (23:59:59.999) in the user's local timezone. */
function endOfDay() {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}
export function getSavedFilters() {
  const s = getStorage();
  if (!s) return {
    regions: []
  };
  const raw = s.getItem(KEYS.savedFilters);
  const obj = safeParse(raw, null);
  if (!obj) return {
    regions: []
  };

  // Auto-clear if the stored filters have passed end-of-day expiry
  if (obj.expiresAt && Date.now() > obj.expiresAt) {
    s.removeItem(KEYS.savedFilters);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("pc-saved-filters-changed"));
    }
    return {
      regions: []
    };
  }

  // Return stored region labels as-is (raw, deduplicated)
  const regions = Array.isArray(obj.regions) ? Array.from(new Set(obj.regions.filter(r => typeof r === "string" && r.trim()))) : [];
  return {
    regions,
    searchQuery: obj.searchQuery,
    discipline: obj.discipline,
    product: obj.product,
    region: obj.region,
    expiresAt: obj.expiresAt
  };
}
export function setSavedFilters(next) {
  const s = getStorage();
  if (!s) return;
  // Store raw region labels, deduped by lowercase
  const seen = new Set();
  const uniqueRaw = (next.regions || []).filter(r => {
    const key = String(r || "").trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const payload = {
    regions: uniqueRaw,
    searchQuery: next.searchQuery,
    discipline: next.discipline,
    product: next.product,
    region: next.region,
    // Always stamp end-of-today so filters auto-clear at midnight
    expiresAt: endOfDay()
  };
  s.setItem(KEYS.savedFilters, JSON.stringify(payload));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("pc-saved-filters-changed"));
  }
}
export function clearSavedFilters() {
  const s = getStorage();
  if (!s) return;
  s.removeItem(KEYS.savedFilters);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("pc-saved-filters-changed"));
  }
}
export function addSavedRegionFilter(label) {
  const saved = getSavedFilters();
  const raw = String(label || "").trim();
  if (!raw) return;
  // Store raw label; dedupe case-insensitively
  const alreadyExists = saved.regions.some(r => r.trim().toLowerCase() === raw.toLowerCase());
  if (!alreadyExists) {
    setSavedFilters({
      regions: [...saved.regions, raw]
    });
  }
}
export function removeSavedRegionFilter(label) {
  const saved = getSavedFilters();
  const raw = String(label || "").trim().toLowerCase();
  if (!raw) return;
  setSavedFilters({
    regions: saved.regions.filter(r => r.trim().toLowerCase() !== raw)
  });
}

// Enumerate all newlyAdded buckets and return all items (optionally filter by type)
export function getAllNewlyAdded(type) {
  const s = getStorage();
  if (!s) return [];
  const out = [];
  for (let i = 0; i < s.length; i++) {
    const key = s.key(i) || "";
    if (key.startsWith("pc_newlyAdded_")) {
      const list = safeParse(s.getItem(key), []);
      for (const item of list) {
        if (!type || item.type === type) out.push(item);
      }
    }
  }
  return out;
}

// Remove approved items from local storage by predicate across all newlyAdded buckets
export function removeApprovedWhere(type, predicate) {
  const s = getStorage();
  if (!s) return 0;
  let removed = 0;
  const keys = [];
  for (let i = 0; i < s.length; i++) {
    const key = s.key(i) || "";
    if (key.startsWith("pc_newlyAdded_")) keys.push(key);
  }
  for (const key of keys) {
    const list = safeParse(s.getItem(key), []);
    const before = list.length;
    const kept = list.filter(it => !(it.type === type && predicate(it)));
    if (kept.length !== before) {
      removed += before - kept.length;
      try {
        s.setItem(key, JSON.stringify(kept));
      } catch {}
    }
  }
  if (removed > 0 && typeof window !== "undefined") {
    window.dispatchEvent(new Event("pc-notifications-changed"));
  }
  return removed;
}

// Remove approved items for any entity type by slug
export function removeApprovedBySlug(type, slug) {
  const norm = String(slug).trim();
  if (!norm) return 0;
  return removeApprovedWhere(type, it => {
    const p = it.payload || {};
    return String(p?.slug ?? "") === norm || String(p?.id ?? "") === norm;
  });
}

// Convenience: remove approved regulation by identifier (slug, id, approval ts, or approved id)
export function removeApprovedRegulation(identifier) {
  const norm = String(identifier);
  return removeApprovedWhere("regulation", it => {
    const p = it.payload || {};
    return String(p?.id ?? "") === norm || String(p?.slug ?? "") === norm || String(it.approvalTimestamp) === norm || String(it.id) === norm;
  });
}

// Rejected helpers
export function addRejected(type, item) {
  const s = getStorage();
  if (!s) throw new Error("Local storage unavailable");
  if (!cookieConsent.allowed()) throw new Error("Cookies not allowed");
  const key = KEYS.rejected(type);
  const list = safeParse(s.getItem(key), []);
  list.push(item);
  s.setItem(key, JSON.stringify(list));
  // Notify listeners
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("pc-notifications-changed"));
  }
}
export function getRejected(type) {
  const s = getStorage();
  if (!s) return [];
  return safeParse(s.getItem(KEYS.rejected(type)), []);
}
export function removeRejectedById(type, id) {
  const s = getStorage();
  if (!s) return 0;
  const list = safeParse(s.getItem(KEYS.rejected(type)), []);
  const next = list.filter(item => String(item.id) !== String(id));
  if (next.length === list.length) return 0;
  s.setItem(KEYS.rejected(type), JSON.stringify(next));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("pc-notifications-changed"));
  }
  return list.length - next.length;
}

// Aggregate helpers
export function getAllPending() {
  const types = ["organization", "event", "regulation", "article"];
  const out = [];
  for (const t of types) out.push(...getPending(t));
  return out.sort((a, b) => a.createdAt - b.createdAt);
}
export function getAllRejected() {
  const types = ["organization", "event", "regulation", "article"];
  const out = [];
  for (const t of types) out.push(...getRejected(t));
  return out.sort((a, b) => a.rejectionTimestamp - b.rejectionTimestamp);
}

// Approve/Reject convenience
export function approvePending(type, id, approverUserId) {
  const list = getPending(type);
  const idx = list.findIndex(p => p.id === id);
  if (idx === -1) return null;
  const [pending] = list.splice(idx, 1);
  replacePending(type, list);
  const isEdit = !!(pending.updatedFields && Object.keys(pending.updatedFields || {}).length > 0);
  let mergedPayload = pending.payload;
  // If this is an edit submission for an organization, try to rebuild full object by overlaying diffs onto a stored baseline
  if (isEdit && type === "organization") {
    try {
      // Attempt to locate a baseline among existing approved items (by id or slug)
      const allApproved = getAllNewlyAdded("organization");
      const baseMatch = allApproved.find(a => pending.baseId && a.payload.id === pending.baseId || pending.baseSlug && a.payload.slug === pending.baseSlug || a.payload.id === pending.id || a.payload.slug === pending.slug);
      let base = baseMatch ? baseMatch.payload : null;
      if (!base) {
        // Fallback to static dataset as baseline (SSR data), matched by baseId/baseSlug or payload id/slug
        try {
          const bid = String(pending.baseId || pending.payload?.id || "");
          const bslug = String(pending.baseSlug || pending.payload?.slug || "");
          const staticMatch = Object.values(organizationsData).find(o => bid && String(o.id) === bid || bslug && String(o.slug) === bslug);
          if (staticMatch) base = staticMatch;
        } catch {}
      }
      const diffs = pending.updatedFields || {};
      if (base && diffs && Object.keys(diffs).length) {
        try {
          if (process.env.NODE_ENV !== "production") console.log("[Admin/Approve] BEFORE", {
            idOrSlug: pending.baseId || pending.baseSlug || pending.payload?.id || pending.payload?.slug,
            basePreview: {
              id: base?.id,
              slug: base?.slug,
              orgName: base?.orgName
            },
            diffs,
            pendingPayload: pending.payload
          });
        } catch {}
        const clone = JSON.parse(JSON.stringify(base));
        const apply = (k, setter) => {
          const d = diffs[k];
          if (d && Object.prototype.hasOwnProperty.call(d, "next")) setter(d.next);
        };
        apply("orgName", v => clone.orgName = v);
        apply("orgDescripTitle", v => clone.orgDescripTitle = v);
        apply("orgDescrip", v => clone.orgDescrip = v);
        // Contact / location
        clone.infoSection = clone.infoSection || {};
        clone.infoSection.contact = clone.infoSection.contact || {
          title: "Contact Us"
        };
        clone.infoSection.location = clone.infoSection.location || {
          title: "Location"
        };
        apply("website", v => clone.infoSection.contact.company = v);
        apply("email", v => clone.infoSection.contact.email = v);
        apply("phone", v => clone.infoSection.contact.phone = v);
        apply("address", v => clone.infoSection.location.address = v);
        apply("city", v => clone.infoSection.location.city = v);
        apply("address2", v => clone.infoSection.location.address2 = v);
        apply("state", v => clone.infoSection.location.state = v);
        apply("postalCode", v => clone.infoSection.location.postalCode = v);
        apply("country", v => clone.infoSection.location.country = v);
        apply("orgType", v => clone.orgType = v);
        apply("orgIndustryFocus", v => clone.orgIndustryFocus = v);
        apply("orgFocus", v => clone.orgFocus = v);
        apply("productFocus", v => clone.productFocus = v);
        apply("regionalFocus", v => clone.regionalFocus = v);
        apply("employees", v => clone.employees = v);
        // Social keys
        clone.infoSection.social = clone.infoSection.social || {
          title: "Follow Us"
        };
        const socialMap = {
          "social.Facebook": "facebook",
          "social.Instagram": "instagram",
          "social.Twitter": "twitter",
          "social.LinkedIn": "linkedIn",
          "social.YouTube": "youtube",
          "social.WhatsApp": "whatsApp",
          "social.TikTok": "tikTok",
          "social.Telegram": "telegram"
        };
        Object.entries(socialMap).forEach(([diffKey, field]) => {
          const d = diffs[diffKey];
          if (d && Object.prototype.hasOwnProperty.call(d, "next")) {
            clone.infoSection.social[field] = d.next;
          }
        });
        // Generic fallback: apply any remaining top-level diffs not explicitly handled above
        const handledTopLevel = new Set(["orgName", "orgDescripTitle", "orgDescrip", "website", "email", "phone", "address", "city", "address2", "state", "postalCode", "country", "orgType", "orgIndustryFocus", "orgFocus", "productFocus", "regionalFocus", "employees"]);
        Object.entries(diffs).forEach(([k, d]) => {
          if (!d || !Object.prototype.hasOwnProperty.call(d, "next")) return;
          if (k === "id" || k === "slug") return; // never override identity
          if (k.includes(".")) return; // dot-paths handled explicitly above
          if (handledTopLevel.has(k)) return; // skip ones we already applied
          try {
            clone[k] = d.next;
          } catch {
            // ignore fallback failures
          }
        });
        mergedPayload = clone;
        try {
          // Emit a concise AFTER snapshot including keys actually changed
          const appliedKeys = Object.keys(diffs).filter(k => Object.prototype.hasOwnProperty.call(diffs[k] || {}, "next"));
          const sample = {
            id: mergedPayload?.id,
            slug: mergedPayload?.slug,
            orgName: mergedPayload?.orgName,
            employees: mergedPayload?.employees
          };
          // Include a few commonly-edited collections for quick verification
          if (appliedKeys.includes("orgType")) sample.orgType = mergedPayload?.orgType;
          if (appliedKeys.includes("orgIndustryFocus")) sample.orgIndustryFocus = mergedPayload?.orgIndustryFocus;
          if (appliedKeys.includes("orgFocus")) sample.orgFocus = mergedPayload?.orgFocus;
          if (appliedKeys.includes("productFocus")) sample.productFocus = mergedPayload?.productFocus;
          if (appliedKeys.includes("regionalFocus")) sample.regionalFocus = mergedPayload?.regionalFocus;
          if (process.env.NODE_ENV !== "production") console.log("[Admin/Approve] AFTER", sample);
        } catch {}
      }
    } catch {
      // fallback: keep pending.payload
    }
  }
  const approved = {
    id: pending.id,
    type,
    payload: mergedPayload,
    approvalTimestamp: Date.now(),
    approverUserId,
    createdBy: pending.createdBy,
    isEdit,
    ...(pending.operation ? {
      operation: pending.operation
    } : {})
  };
  try {
    addApproved(approved);
  } catch (e) {
    // Fallback persistence when cookie consent blocks standard path
    try {
      const s = getStorage();
      if (s) {
        const day = new Date(approved.approvalTimestamp).toISOString().slice(0, 10);
        const key = KEYS.newlyAdded(day);
        const list = safeParse(s.getItem(key), []);
        // Basic de-dupe in-bucket by id/slug
        const payloadAny = approved.payload || {};
        const newId = String((payloadAny.id ?? approved.id) || "");
        const newSlug = String(payloadAny.slug || "");
        const filtered = list.filter(entry => {
          const p = entry.payload || {};
          const idMatch = newId && String(p.id || entry.id) === newId;
          const slugMatch = newSlug && String(p.slug || "") === newSlug;
          return !(entry.type === type && (idMatch || slugMatch));
        });
        filtered.push(approved);
        s.setItem(key, JSON.stringify(filtered));
        s.setItem(KEYS.version, `${day}:${approved.id}`);
        if (typeof window !== "undefined") {
          window.dispatchEvent(new Event("pc-notifications-changed"));
        }
      }
    } catch {
      // ignore
    }
  }
  // Persist latest merged payload snapshot for quick overlay even without diff context
  try {
    const s = getStorage();
    if (s && type === "organization") {
      const keyStore = KEYS.latestOverlay(type);
      const map = safeParse(s.getItem(keyStore), {});
      const payloadAny = approved.payload || {};
      const oid = payloadAny.id ? String(payloadAny.id) : "";
      const slug = payloadAny.slug ? String(payloadAny.slug) : "";
      if (oid) map[`id:${oid}`] = payloadAny;
      if (slug) map[`slug:${slug.toLowerCase()}`] = payloadAny;
      s.setItem(keyStore, JSON.stringify(map));
      // Mirror to sessionStorage for ephemeral overlays (when cookies are restricted)
      try {
        const sess = typeof window !== "undefined" ? window.sessionStorage : null;
        if (sess) {
          const sessMap = safeParse(sess.getItem(keyStore), {});
          if (oid) sessMap[`id:${oid}`] = payloadAny;
          if (slug) sessMap[`slug:${slug.toLowerCase()}`] = payloadAny;
          sess.setItem(keyStore, JSON.stringify(sessMap));
        }
      } catch {}
      // Notify listeners so pages can refresh overlays immediately
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("pc-approved-overlay-updated"));
        // Emit a targeted cache invalidation event so open org pages can refresh
        try {
          const detail = {
            type,
            id: oid || approved.id,
            slug
          };
          window.dispatchEvent(new CustomEvent("pc-org-cache-invalidated", {
            detail
          }));
        } catch {}
      }
    }
  } catch {
    /* ignore persistence errors */
  }
  return approved;
}

// Approve a specific pending item when there may be duplicate ids by using createdAt as a tiebreaker.
export function approvePendingExact(type, id, createdAt, approverUserId) {
  const list = getPending(type);
  let idx = -1;
  if (typeof createdAt === "number") {
    idx = list.findIndex(p => p.id === id && Number(p.createdAt) === Number(createdAt));
  }
  if (idx === -1) idx = list.findIndex(p => p.id === id);
  if (idx === -1) return null;
  const [pending] = list.splice(idx, 1);
  replacePending(type, list);
  const isEdit = !!(pending.updatedFields && Object.keys(pending.updatedFields || {}).length > 0);
  let mergedPayload = pending.payload;
  if (isEdit && type === "organization") {
    try {
      const allApproved = getAllNewlyAdded("organization");
      const baseMatch = allApproved.find(a => pending.baseId && a.payload.id === pending.baseId || pending.baseSlug && a.payload.slug === pending.baseSlug || a.payload.id === pending.id || a.payload.slug === pending.slug);
      let base = baseMatch ? baseMatch.payload : null;
      if (!base) {
        try {
          const bid = String(pending.baseId || pending.payload?.id || "");
          const bslug = String(pending.baseSlug || pending.payload?.slug || "");
          const staticMatch = Object.values(organizationsData).find(o => bid && String(o.id) === bid || bslug && String(o.slug) === bslug);
          if (staticMatch) base = staticMatch;
        } catch {}
      }
      const diffs = pending.updatedFields || {};
      if (base && diffs && Object.keys(diffs).length) {
        try {
          if (process.env.NODE_ENV !== "production") console.log("[Admin/ApproveExact] BEFORE", {
            idOrSlug: pending.baseId || pending.baseSlug || pending.payload?.id || pending.payload?.slug
          });
        } catch {}
        const clone = JSON.parse(JSON.stringify(base));
        const apply = (k, setter) => {
          const d = diffs[k];
          if (d && Object.prototype.hasOwnProperty.call(d, "next")) setter(d.next);
        };
        apply("orgName", v => clone.orgName = v);
        apply("orgDescripTitle", v => clone.orgDescripTitle = v);
        apply("orgDescrip", v => clone.orgDescrip = v);
        clone.infoSection = clone.infoSection || {};
        clone.infoSection.contact = clone.infoSection.contact || {
          title: "Contact Us"
        };
        clone.infoSection.location = clone.infoSection.location || {
          title: "Location"
        };
        apply("website", v => clone.infoSection.contact.company = v);
        apply("email", v => clone.infoSection.contact.email = v);
        apply("phone", v => clone.infoSection.contact.phone = v);
        apply("address", v => clone.infoSection.location.address = v);
        apply("city", v => clone.infoSection.location.city = v);
        apply("address2", v => clone.infoSection.location.address2 = v);
        apply("state", v => clone.infoSection.location.state = v);
        apply("postalCode", v => clone.infoSection.location.postalCode = v);
        apply("country", v => clone.infoSection.location.country = v);
        apply("orgType", v => clone.orgType = v);
        apply("orgIndustryFocus", v => clone.orgIndustryFocus = v);
        apply("orgFocus", v => clone.orgFocus = v);
        apply("productFocus", v => clone.productFocus = v);
        apply("regionalFocus", v => clone.regionalFocus = v);
        apply("employees", v => clone.employees = v);
        clone.infoSection.social = clone.infoSection.social || {
          title: "Follow Us"
        };
        const socialMap = {
          "social.Facebook": "facebook",
          "social.Instagram": "instagram",
          "social.Twitter": "twitter",
          "social.LinkedIn": "linkedIn",
          "social.YouTube": "youtube",
          "social.WhatsApp": "whatsApp",
          "social.TikTok": "tikTok",
          "social.Telegram": "telegram"
        };
        Object.entries(socialMap).forEach(([diffKey, field]) => {
          const d = diffs[diffKey];
          if (d && Object.prototype.hasOwnProperty.call(d, "next")) {
            clone.infoSection.social[field] = d.next;
          }
        });
        const handledTopLevel = new Set(["orgName", "orgDescripTitle", "orgDescrip", "website", "email", "phone", "address", "city", "address2", "state", "postalCode", "country", "orgType", "orgIndustryFocus", "orgFocus", "productFocus", "regionalFocus", "employees"]);
        Object.entries(diffs).forEach(([k, d]) => {
          if (!d || !Object.prototype.hasOwnProperty.call(d, "next")) return;
          if (k === "id" || k === "slug") return;
          if (k.includes(".")) return;
          if (handledTopLevel.has(k)) return;
          try {
            clone[k] = d.next;
          } catch {}
        });
        mergedPayload = clone;
      }
    } catch {}
  }
  const approved = {
    id: pending.id,
    type,
    payload: mergedPayload,
    approvalTimestamp: Date.now(),
    approverUserId,
    createdBy: pending.createdBy,
    isEdit,
    ...(pending.operation ? {
      operation: pending.operation
    } : {})
  };
  try {
    addApproved(approved);
  } catch (e) {
    try {
      const s = getStorage();
      if (s) {
        const day = new Date(approved.approvalTimestamp).toISOString().slice(0, 10);
        const key = KEYS.newlyAdded(day);
        const list = safeParse(s.getItem(key), []);
        const payloadAny = approved.payload || {};
        const newId = String((payloadAny.id ?? approved.id) || "");
        const newSlug = String(payloadAny.slug || "");
        const filtered = list.filter(entry => {
          const p = entry.payload || {};
          const idMatch = newId && String(p.id || entry.id) === newId;
          const slugMatch = newSlug && String(p.slug || "") === newSlug;
          return !(entry.type === type && (idMatch || slugMatch));
        });
        filtered.push(approved);
        s.setItem(key, JSON.stringify(filtered));
        s.setItem(KEYS.version, `${day}:${approved.id}`);
        if (typeof window !== "undefined") {
          window.dispatchEvent(new Event("pc-notifications-changed"));
        }
      }
    } catch {}
  }
  try {
    const s = getStorage();
    if (s && type === "organization") {
      const keyStore = KEYS.latestOverlay(type);
      const map = safeParse(s.getItem(keyStore), {});
      const payloadAny = approved.payload || {};
      const oid = payloadAny.id ? String(payloadAny.id) : "";
      const slug = payloadAny.slug ? String(payloadAny.slug) : "";
      if (oid) map[`id:${oid}`] = payloadAny;
      if (slug) map[`slug:${slug.toLowerCase()}`] = payloadAny;
      s.setItem(keyStore, JSON.stringify(map));
      try {
        const sess = typeof window !== "undefined" ? window.sessionStorage : null;
        if (sess) {
          const sessMap = safeParse(sess.getItem(keyStore), {});
          if (oid) sessMap[`id:${oid}`] = payloadAny;
          if (slug) sessMap[`slug:${slug.toLowerCase()}`] = payloadAny;
          sess.setItem(keyStore, JSON.stringify(sessMap));
        }
      } catch {}
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("pc-approved-overlay-updated"));
        try {
          const detail = {
            type,
            id: oid || approved.id,
            slug
          };
          window.dispatchEvent(new CustomEvent("pc-org-cache-invalidated", {
            detail
          }));
        } catch {}
      }
    }
  } catch {}
  return approved;
}
export function rejectPendingExact(type, id, createdAt, rejectedBy, reason) {
  const list = getPending(type);
  let idx = -1;
  if (typeof createdAt === "number") {
    idx = list.findIndex(p => p.id === id && Number(p.createdAt) === Number(createdAt));
  }
  if (idx === -1) idx = list.findIndex(p => p.id === id);
  if (idx === -1) return null;
  const [pending] = list.splice(idx, 1);
  replacePending(type, list);
  const rejected = {
    id: pending.id,
    type,
    payload: pending.payload,
    rejectionTimestamp: Date.now(),
    rejectedBy,
    reason,
    createdBy: pending.createdBy
  };
  addRejected(type, rejected);
  return rejected;
}
export function rejectPending(type, id, rejectedBy, reason) {
  const list = getPending(type);
  const idx = list.findIndex(p => p.id === id);
  if (idx === -1) return null;
  const [pending] = list.splice(idx, 1);
  replacePending(type, list);
  const rejected = {
    id: pending.id,
    type,
    payload: pending.payload,
    rejectionTimestamp: Date.now(),
    rejectedBy,
    reason,
    createdBy: pending.createdBy
  };
  addRejected(type, rejected);
  return rejected;
}

// Minimal user id utility for associating drafts with a user
export function getCurrentUserId() {
  const s = getStorage();
  if (!s) return "user_anonymous";
  let id = s.getItem(KEYS.userId);
  if (!id) {
    id = `user_${Math.random().toString(36).slice(2, 10)}`;
    s.setItem(KEYS.userId, id);
  }
  return id;
}

/**
 * Build a human-readable author name from the authenticated user object.
 * Falls back through displayName → firstName+lastName → email → random userId.
 */
export function getAuthorDisplayName(user) {
  if (user?.displayName) return user.displayName;
  const first = user?.profile?.firstName?.trim() ?? "";
  const last = user?.profile?.lastName?.trim() ?? "";
  const full = [first, last].filter(Boolean).join(" ");
  if (full) return full;
  if (user?.email) return user.email;
  return getCurrentUserId();
}

/**
 * Check if the current user authored a given resource by looking at
 * approved items in localStorage.
 */
export function isPageAuthor(type, slugOrId) {
  if (!slugOrId) return false;
  const userId = getCurrentUserId();
  if (!userId || userId === "user_anonymous") return false;
  try {
    const items = getAllNewlyAdded(type);
    const lower = slugOrId.toLowerCase();
    return items.some(item => {
      if (!item.createdBy || item.createdBy !== userId) return false;
      const payload = item.payload || {};
      const id = String(payload.id || payload.slug || "").toLowerCase();
      const slug = String(payload.slug || "").toLowerCase();
      return id === lower || slug === lower;
    });
  } catch {
    return false;
  }
}
const notificationMatchesScope = (createdBy, userId, includeAll) => includeAll || !!createdBy && createdBy === userId;
export function getUserNotifications(userId, options) {
  const includeAll = options?.includeAll ?? false;
  const approvals = getAllNewlyAdded().filter(a => notificationMatchesScope(a.createdBy, userId, includeAll)).length;
  const rejections = getAllRejected().filter(r => notificationMatchesScope(r.createdBy, userId, includeAll)).length;
  return {
    approvals,
    rejections
  };
}

// Retrieve latest approved overlay payload for a given entity id or slug
export function getLatestApprovedOverlay(type, identifier) {
  const s = getStorage();
  const exactIdKey = `id:${identifier}`;
  const exactSlugKey = `slug:${identifier}`;
  const lowerSlugKey = `slug:${identifier.toLowerCase()}`;
  const read = store => store ? safeParse(store.getItem(KEYS.latestOverlay(type)), {}) : {};
  const mapLocal = read(s);
  const hitLocal = mapLocal[exactIdKey] || mapLocal[exactSlugKey] || mapLocal[lowerSlugKey];
  if (hitLocal) return hitLocal;
  // Session fallback
  if (typeof window !== "undefined") {
    const mapSession = read(window.sessionStorage);
    return mapSession[exactIdKey] || mapSession[exactSlugKey] || mapSession[lowerSlugKey] || null;
  }
  return null;
}

// User notifications unread helpers
export function getNotificationsLastSeen(userId) {
  const s = getStorage();
  if (!s) return 0;
  const raw = s.getItem(KEYS.notificationsLastSeen(userId));
  const n = raw ? Number(raw) : 0;
  return Number.isFinite(n) ? n : 0;
}
export function setNotificationsLastSeen(userId, ts) {
  const s = getStorage();
  if (!s) return;
  s.setItem(KEYS.notificationsLastSeen(userId), String(ts));
}
export function getUserNotificationsUnreadCount(userId, options) {
  const includeAll = options?.includeAll ?? false;
  const lastSeen = getNotificationsLastSeen(userId);
  const approvals = getAllNewlyAdded().filter(a => a.approvalTimestamp > lastSeen).filter(a => notificationMatchesScope(a.createdBy, userId, includeAll)).length;
  const rejections = getAllRejected().filter(r => r.rejectionTimestamp > lastSeen).filter(r => notificationMatchesScope(r.createdBy, userId, includeAll)).length;
  return approvals + rejections;
}

// Simple cross-route flash message helpers
export function setFlashMessage(message, severity = "success") {
  store.dispatch(pushFlash(message, severity));
}

// Prefill rejected item into create page
export function setCreatePrefill(prefill) {
  const s = getStorage();
  if (!s) return;
  s.setItem(KEYS.createPrefill, JSON.stringify(prefill));
}
export function getCreatePrefill() {
  const s = getStorage();
  if (!s) return null;
  const raw = s.getItem(KEYS.createPrefill);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
export function clearCreatePrefill() {
  const s = getStorage();
  if (!s) return;
  s.removeItem(KEYS.createPrefill);
}

// Regulation version history (client-side only)

export function getRegulationVersions(regId) {
  const s = getStorage();
  if (!s) return [];
  const raw = s.getItem(KEYS.regulationVersions(regId));
  return safeParse(raw, []);
}
export function addRegulationVersion(regId, entry) {
  const s = getStorage();
  if (!s) return;
  const key = KEYS.regulationVersions(regId);
  const list = safeParse(s.getItem(key), []);
  list.unshift(entry);
  s.setItem(key, JSON.stringify(list));
}

// ---------------- Admin feature flags -----------------
/** True only when running on localhost/127.0.0.1 — the gate for
 *  local-only conveniences (random generation, placeholder toggles).
 *  UI surfaces gated by this should carry <LocalOnlyBadge/>. */
export function isLocalEnvironment() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1";
}
export function getAllowRandomGeneration() {
  // Random data generation is a LOCAL-DEV convenience only — the admin
  // toggle was removed, and the feature is now available solely on
  // localhost/127.0.0.1, never on a deployed environment.
  return isLocalEnvironment();
}

/** Retained only so any legacy caller of the old parser still links —
 *  no longer reachable from the app. */
export function setAllowRandomGeneration(value) {
  const s = getStorage();
  if (!s) return;
  s.setItem(KEYS.allowRandomGeneration, value ? "1" : "0");
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("pc-admin-flags-changed"));
  }
}
export function getEnableCreateProdcertOrg() {
  const s = getStorage();
  if (!s) return false;
  const raw = s.getItem(KEYS.enableCreateProdcertOrg);
  if (raw === null) return false;
  if (raw === "1" || raw === "true") return true;
  if (raw === "0" || raw === "false") return false;
  try {
    return !!JSON.parse(raw);
  } catch {
    return false;
  }
}
export function setEnableCreateProdcertOrg(value) {
  const s = getStorage();
  if (!s) return;
  s.setItem(KEYS.enableCreateProdcertOrg, value ? "1" : "0");
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("pc-admin-flags-changed"));
  }
}
export function getEnableRandomEventPrefill() {
  const s = getStorage();
  if (!s) return false;
  const raw = s.getItem(KEYS.enableRandomEventPrefill);
  if (raw === null) return false;
  if (raw === "1" || raw === "true") return true;
  if (raw === "0" || raw === "false") return false;
  try {
    return !!JSON.parse(raw);
  } catch {
    return false;
  }
}
export function setEnableRandomEventPrefill(value) {
  const s = getStorage();
  if (!s) return;
  s.setItem(KEYS.enableRandomEventPrefill, value ? "1" : "0");
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("pc-admin-flags-changed"));
  }
}

// Merge newlyAdded into base collection by oldest approval first
export function mergeNewlyAdded(base, newly, options) {
  if (newly.length > 0) {
    trackMerge(options?.trackAs ?? "items", newly.length);
  }
  const sorted = [...newly].sort((a, b) => a.approvalTimestamp - b.approvalTimestamp);
  const indexByKey = new Map();
  const makeKeys = (item, fallbackIdx) => {
    const keys = [];
    if (item?.id) keys.push(`id:${String(item.id)}`);
    if (item?.slug) keys.push(`slug:${String(item.slug)}`);
    if (!keys.length && typeof fallbackIdx === "number") keys.push(`idx:${fallbackIdx}`);
    return keys;
  };
  base.forEach((item, idx) => {
    for (const k of makeKeys(item, idx)) indexByKey.set(k, idx);
  });
  const out = [...base];
  for (const n of sorted) {
    const p = n.payload || {};
    const withFlags = {
      ...p,
      ...(options?.markNew ? {
        isNew: true,
        approvalTimestamp: n.approvalTimestamp
      } : {}),
      ...(typeof n.isEdit === "boolean" ? {
        isEdit: n.isEdit
      } : {})
    };
    const keys = makeKeys(p);
    let replaced = false;
    for (const k of keys) {
      if (indexByKey.has(k)) {
        const idx = indexByKey.get(k);
        out[idx] = withFlags;
        // Update index keys for this slot in case slug/id changed
        for (const oldKey of makeKeys(out[idx], idx)) indexByKey.set(oldKey, idx);
        replaced = true;
        break;
      }
    }
    if (!replaced) {
      const idx = out.push(withFlags) - 1;
      for (const k of keys.length ? keys : makeKeys(withFlags, idx)) indexByKey.set(k, idx);
    }
  }
  // Final de-dupe pass by slug then id (prefer newest by last occurrence)
  const seenSlug = new Set();
  const seenId = new Set();
  const deduped = [];
  for (let i = out.length - 1; i >= 0; i--) {
    const it = out[i] || {};
    const slug = it.slug ? String(it.slug) : "";
    const id = it.id ? String(it.id) : "";
    const slugKey = slug ? `slug:${slug}` : "";
    const idKey = id ? `id:${id}` : "";
    if (slugKey && seenSlug.has(slugKey) || idKey && seenId.has(idKey)) {
      continue; // skip older duplicate
    }
    if (slugKey) seenSlug.add(slugKey);
    if (idKey) seenId.add(idKey);
    deduped.unshift(it);
  }
  return deduped;
}

// ────────────── Tag preferences (cross-page sync) ──────────────────────────
const TAG_PREFS_KEY = "pc_tagPreferences";
const emptyTagPrefs = {
  regions: [],
  disciplines: [],
  products: []
};
export function getTagPreferences() {
  try {
    const raw = localStorage.getItem(TAG_PREFS_KEY);
    if (!raw) return {
      ...emptyTagPrefs
    };
    const parsed = JSON.parse(raw);
    return {
      regions: Array.isArray(parsed.regions) ? parsed.regions : [],
      disciplines: Array.isArray(parsed.disciplines) ? parsed.disciplines : [],
      products: Array.isArray(parsed.products) ? parsed.products : []
    };
  } catch {
    return {
      ...emptyTagPrefs
    };
  }
}
export function setTagPreferences(prefs) {
  try {
    localStorage.setItem(TAG_PREFS_KEY, JSON.stringify(prefs));
    // Dispatch a storage event so other tabs / hooks can react
    window.dispatchEvent(new StorageEvent("storage", {
      key: TAG_PREFS_KEY,
      newValue: JSON.stringify(prefs)
    }));
    // Custom event so same-tab listeners (e.g. org index page) can react
    window.dispatchEvent(new Event("pc-tag-preferences-changed"));
  } catch {
    // quota errors — ignore
  }
}

/** Toggle a single tag in the given category and persist. Returns the new set. */
export function toggleTagPreference(category, tag) {
  const prefs = getTagPreferences();
  const arr = prefs[category];
  const idx = arr.indexOf(tag);
  if (idx >= 0) {
    arr.splice(idx, 1);
  } else {
    arr.push(tag);
  }
  setTagPreferences(prefs);
  return [...arr];
}

// ── Follow-tag confirmation preference ─────────────────────────────────
// When true, tapping a tag chip follows/unfollows immediately instead of
// showing the confirmation dialog. Surfaced as a checkbox in the dialog
// ("Don't show this again") and as a switch in the profile modal.
const SKIP_FOLLOW_CONFIRM_KEY = "pc_skipFollowTagConfirm";
export const FOLLOW_CONFIRM_PREF_EVENT = "pc-follow-confirm-pref-changed";
export function getSkipFollowTagConfirm() {
  const s = getStorage();
  if (!s) return false;
  try {
    return s.getItem(SKIP_FOLLOW_CONFIRM_KEY) === "true";
  } catch {
    return false;
  }
}
export function setSkipFollowTagConfirm(skip) {
  const s = getStorage();
  if (!s) return;
  try {
    if (skip) s.setItem(SKIP_FOLLOW_CONFIRM_KEY, "true");else s.removeItem(SKIP_FOLLOW_CONFIRM_KEY);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event(FOLLOW_CONFIRM_PREF_EVENT));
    }
  } catch {
    /* storage unavailable */
  }
}

// ── Index page data cache ────────────────────────────────────────────────────

const INDEX_CACHE_PREFIX = "pc_index_cache_";
const CACHE_SESSION_KEY = "pc_cache_session";
const STALE_MS = 24 * 60 * 60 * 1000; // 24 hours

// Server data-version key (mirrors useDataVersionCheck.ts). Caches are
// stamped with the version present at write time; reads can use the stamp
// to detect that newer data exists on the server without wiping the cache.
const DATA_VERSION_KEY_PREFIX = "dataVersion:";

/** Read the current locally-known server data version for an entity type. */
export function getCurrentDataVersion(type) {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(`${DATA_VERSION_KEY_PREFIX}${type}`);
    if (raw === null) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/** Map a detail-cache type back to its index/entity type for version lookup. */
function detailTypeToIndexType(type) {
  switch (type) {
    case "organization":
      return "organizations";
    case "event":
      return "events";
    case "regulation":
      return "regulations";
    case "article":
      return "articles";
    case "professional":
      return "professionals";
    default:
      return null;
  }
}

/**
 * Module-level in-memory mirror of localStorage cache.
 * Empty on SSR and on hard refresh (new module execution) so getIndexCache()
 * returns null during hydration — matching the server-rendered HTML.
 * Populated after the first setIndexCache / getIndexCache hit, so subsequent
 * client-side navigations get instant data without a skeleton flash.
 */
const memoryCache = new Map();

/**
 * Guard flag: stays false until after the first client-side paint.
 * While false, getIndexCache() only returns from the memoryCache Map
 * (which is empty on hard refresh) — never from localStorage — so the
 * first client render matches the server-rendered HTML.
 * After hydration, we promote localStorage entries into the Map so that
 * subsequent client-side navigations are instant.
 */
let hydrated = false;
if (typeof window !== "undefined") {
  // Schedule promotion right after React hydration completes.
  // setTimeout(..., 0) fires after the current synchronous render + commit.
  setTimeout(() => {
    hydrated = true;
    // Warm the memory map from localStorage for every cache type so the
    // next client-side navigation to an index page is instant.
    const types = ["organizations", "events", "regulations", "articles"];
    for (const t of types) {
      if (!memoryCache.has(t)) {
        // Call getIndexCache now that hydrated=true to promote from LS
        getIndexCache(t);
      }
    }
  }, 0);
}

/** Get or create a session fingerprint scoped to the current login session. */
function getCacheSessionId() {
  try {
    const existing = localStorage.getItem(CACHE_SESSION_KEY);
    if (existing) return existing;
    const id = Date.now().toString(36);
    localStorage.setItem(CACHE_SESSION_KEY, id);
    return id;
  } catch {
    return "";
  }
}

/** Reset the session fingerprint (call on login / logout). */
export function resetCacheSession() {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(CACHE_SESSION_KEY);
  } catch {
    /* SSR */
  }
}

/**
 * Get cached index page data.
 * Returns from in-memory map instantly when available (client navigations).
 * Falls back to localStorage; checks freshness:
 *   - Logged-in user: stale if sessionId differs from current session
 *   - Anonymous user: stale if timestamp > 24 hours old
 * Returns null (and auto-clears storage) when stale.
 */
// Set by getIndexCacheInner so the tracking wrapper can label a hit as
// coming from the in-memory map vs localStorage.
let lastIndexReadSource = "storage";

/** Tracking wrapper — see {@link getIndexCacheInner} for the real logic. */
export function getIndexCache(type) {
  const data = getIndexCacheInner(type);
  trackCacheRead(`index:${type}`, data != null, data != null ? lastIndexReadSource : "storage");
  return data;
}
function getIndexCacheInner(type) {
  if (typeof window === "undefined") return null;

  // Fast path: in-memory hit (populated during this page session)
  const mem = memoryCache.get(type);
  if (mem) {
    lastIndexReadSource = "memory";
    return mem;
  }

  // Before hydration completes, never read from localStorage — the server
  // rendered with no cache, so the client's first render must match.
  if (!hydrated) return null;

  // Only read from localStorage when the user has accepted cookies.
  if (!cookieConsent.allowed()) return null;
  try {
    const raw = localStorage.getItem(`${INDEX_CACHE_PREFIX}${type}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed.data)) return null;

    // Freshness check
    const hasToken = !!localStorage.getItem("pc_auth_token");
    if (hasToken) {
      // Logged-in: compare session IDs
      const currentSession = getCacheSessionId();
      if (!parsed.sessionId || parsed.sessionId !== currentSession) {
        localStorage.removeItem(`${INDEX_CACHE_PREFIX}${type}`);
        return null;
      }
    } else {
      // Anonymous: 24-hour TTL
      const age = Date.now() - (parsed.timestamp ?? 0);
      if (age > STALE_MS) {
        localStorage.removeItem(`${INDEX_CACHE_PREFIX}${type}`);
        return null;
      }
    }

    // Fresh — populate memory map for instant access on next navigation
    memoryCache.set(type, parsed.data);
    lastIndexReadSource = "storage";
    return parsed.data;
  } catch {
    return null;
  }
}

/** Cache index page data with session ID and timestamp. */
export function setIndexCache(type, data) {
  if (typeof window === "undefined") return;
  memoryCache.set(type, data);
  trackCacheWrite(`index:${type}`, data.length, cookieConsent.allowed() ? "storage" : "memory");
  if (!cookieConsent.allowed()) return;
  try {
    localStorage.setItem(`${INDEX_CACHE_PREFIX}${type}`, JSON.stringify({
      data,
      sessionId: getCacheSessionId(),
      timestamp: Date.now(),
      dataVersion: getCurrentDataVersion(type)
    }));
  } catch {
    // quota errors — ignore
  }
}

/**
 * Clear all index page caches (or a specific one).
 *
 * Heavy reset: nukes both in-memory map and localStorage AND wipes detail
 * caches. Use only for hard transitions (logout, manual cache clear). For
 * "the server bumped its version" use {@link markIndexCachesStale} which
 * preserves the cache so users still see content while the refetch runs.
 */
export function clearIndexCaches(type) {
  if (typeof window === "undefined") return;
  const types = type ? [type] : ["organizations", "events", "regulations", "articles", "professionals"];
  for (const t of types) {
    memoryCache.delete(t);
    try {
      localStorage.removeItem(`${INDEX_CACHE_PREFIX}${t}`);
    } catch {}
  }
  // Also clear content-counts when clearing orgs or all caches
  if (!type || type === "organizations") {
    contentCountsMemory = null;
    try {
      localStorage.removeItem(CONTENT_COUNTS_KEY);
    } catch {}
  }
  // Also purge detail-page caches for the matching entity type(s).
  // Keeping detail caches while clearing index caches causes stale
  // detail pages (e.g. old images after an approved edit).
  const detailTypeFor = t => {
    switch (t) {
      case "organizations":
        return "organization";
      case "events":
        return "event";
      case "regulations":
        return "regulation";
      case "articles":
        return "article";
      case "professionals":
        return "professional";
      default:
        return null;
    }
  };
  for (const t of types) {
    const dt = detailTypeFor(t);
    if (dt) clearDetailCaches(dt);
  }
}

/**
 * Soft-invalidate caches for an entity type (or all types).
 *
 * Unlike {@link clearIndexCaches}, this preserves the cached data so the
 * UI can keep rendering it while a background refetch runs. Detail caches
 * for the matching entity become "stale" automatically because they're
 * version-stamped — {@link getDetailCache} compares the stamp against the
 * current data version and returns null when the cache predates a bump.
 *
 * Call this in response to server version bumps (e.g. SSE notifications).
 */
export function markIndexCachesStale(type) {
  if (typeof window === "undefined") return;
  // No-op for storage: keep index data so the UI doesn't flash empty
  // while RTK Query refetches. Detail caches are version-gated on read.
  void type;
}

/**
 * Hard reset of all client-side state EXCEPT authentication and cookie
 * consent. Used by the "Refresh Data" admin action so a single click
 * returns the user to a clean slate without forcing a re-login or a
 * fresh cookie banner.
 *
 * Wipes:
 *   - All `pc_*` localStorage keys (index/detail caches, deactivated org
 *     set, saved filters, pending submissions, newly-added overlays,
 *     rejected lists, regulation versions, dev flags, content counts,
 *     latest overlays, flash messages, create prefill, version stamps,
 *     cache session id, etc.)
 *   - All `dataVersion:*` keys
 *   - In-memory caches (memoryCache, detailMemoryCache(+Meta),
 *     contentCountsMemory)
 *
 * Preserves:
 *   - `pc_auth_token` (so the user stays signed in)
 *   - `pc_auth_consent` (session-token consent, paired with the auth token)
 *   - `pc_cookie_prefs` (so consent doesn't re-prompt)
 *   - `pc_user_id` (paired with auth)
 */
export function resetClientState() {
  if (typeof window === "undefined") return;

  // Preserve these across the wipe.
  const PRESERVE = new Set(["pc_auth_token", "pc_auth_consent", "pc_cookie_prefs", "pc_user_id",
  // Followed tags are a user preference, not a cache — wiping them
  // silently emptied the profile toggles + relevance sections.
  "pc_tagPreferences"]);
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (PRESERVE.has(k)) continue;
      // Wipe everything we own. Two key families:
      //  - `pc_*`        : app state (caches, prefs, dev flags, …)
      //  - `dataVersion:*`: per-entity SSE version stamps
      if (k.startsWith("pc_") || k.startsWith("dataVersion:")) {
        toRemove.push(k);
      }
    }
    for (const k of toRemove) {
      try {
        localStorage.removeItem(k);
      } catch {
        /* ignore single-key failures */
      }
    }
  } catch {
    /* SSR / disabled storage — nothing to do */
  }

  // Drop in-memory caches so subsequent reads don't repopulate from RAM.
  try {
    memoryCache.clear();
  } catch {
    /* ignore */
  }
  try {
    detailMemoryCache.clear();
    detailMemoryCacheMeta.clear();
  } catch {
    /* ignore */
  }
  contentCountsMemory = null;
  // Same clean-slate contract as contentCountsMemory: the promotions
  // memory map (and its generation guard against in-flight writes) must
  // not survive a Refresh Data.
  clearPromotionsCache();

  // Notify any listeners (e.g. deactivated-orgs subscribers) so derived
  // UI state recomputes immediately.
  try {
    window.dispatchEvent(new Event("pc-deactivated-changed"));
  } catch {
    /* ignore */
  }
}

// ── Organization content counts cache ────────────────────────────────────────

const CONTENT_COUNTS_KEY = "pc_org_content_counts";
/**
 * Content counts change only when one of these entity types changes, so a
 * cache written under matching data versions is current by construction.
 * All four must be known (non-null) and equal for the stamp to validate —
 * an unknown version means "can't prove freshness", which is a miss.
 */
const CONTENT_COUNT_VERSION_TYPES = ["organizations", "events", "articles", "regulations"];
/**
 * The freshest locally-known server version for an entity, reconciling BOTH
 * local sources: the acknowledged `dataVersion:` key AND the persisted
 * /data-versions token (ignored once expired). Versions are monotonically
 * increasing counters, so the max of the two is always the freshest — taking
 * the key alone would let a stale acknowledgment shadow a renewed token on
 * pages whose version-check hook never mounts (e.g. promotions on /events/),
 * serving deleted content until an unrelated page visit. Token key literal
 * matches VERSIONS_TOKEN_KEY in useDataVersionCheck (not imported: that
 * would be a util↔hook cycle).
 */
function currentKnownVersion(entity) {
  if (typeof window === "undefined") return null;
  let acknowledged = null;
  let fromToken = null;
  try {
    const raw = localStorage.getItem(`${DATA_VERSION_KEY_PREFIX}${entity}`);
    if (raw !== null) {
      const n = parseInt(raw, 10);
      if (Number.isFinite(n)) acknowledged = n;
    }
  } catch {}
  try {
    const rawToken = localStorage.getItem("pc_data_versions_token_v1");
    if (rawToken) {
      const parsed = JSON.parse(rawToken);
      if (typeof parsed?.expiresAt === "number" && Date.now() < parsed.expiresAt && typeof parsed?.versions?.[entity] === "number") {
        fromToken = parsed.versions[entity];
      }
    }
  } catch {}
  if (acknowledged === null) return fromToken;
  if (fromToken === null) return acknowledged;
  return Math.max(acknowledged, fromToken);
}
function currentContentCountsStamp() {
  const stamp = {};
  for (const t of CONTENT_COUNT_VERSION_TYPES) {
    stamp[t] = currentKnownVersion(t);
  }
  return stamp;
}
function contentCountsStampFresh(stamp) {
  if (!stamp || typeof stamp !== "object") return false;
  const s = stamp;
  return CONTENT_COUNT_VERSION_TYPES.every(t => s[t] != null && s[t] === currentKnownVersion(t));
}
let contentCountsMemory = null;
let lastCountsReadSource = "storage";
function readContentCountsValidated(requireHydrated) {
  if (typeof window === "undefined") return null;
  if (contentCountsMemory) {
    if (contentCountsStampFresh(contentCountsMemory.versions)) {
      lastCountsReadSource = "memory";
      return contentCountsMemory.data;
    }
    contentCountsMemory = null;
  }
  if (requireHydrated && !hydrated) return null;
  if (!cookieConsent.allowed()) return null;
  try {
    const raw = localStorage.getItem(CONTENT_COUNTS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.data !== "object") return null;
    const hasToken = !!localStorage.getItem("pc_auth_token");
    if (hasToken) {
      const currentSession = getCacheSessionId();
      if (!parsed.sessionId || parsed.sessionId !== currentSession) {
        localStorage.removeItem(CONTENT_COUNTS_KEY);
        return null;
      }
    } else {
      const age = Date.now() - (parsed.timestamp ?? 0);
      if (age > STALE_MS) {
        localStorage.removeItem(CONTENT_COUNTS_KEY);
        return null;
      }
    }
    if (!contentCountsStampFresh(parsed.versions)) {
      // Written under older entity versions (or before stamping
      // existed) — the counts may have changed. Miss.
      localStorage.removeItem(CONTENT_COUNTS_KEY);
      return null;
    }
    contentCountsMemory = {
      data: parsed.data,
      versions: parsed.versions
    };
    lastCountsReadSource = "storage";
    return parsed.data;
  } catch {
    return null;
  }
}

/** Render-time read (hydration-gated so the first client render matches
 *  the server-rendered HTML). */
export function getContentCountsCache() {
  return readContentCountsValidated(true);
}

/**
 * RTK queryFn read path. Hydration consistency doesn't apply to async
 * query results, so this skips the `hydrated` gate — which flips in a
 * setTimeout AFTER first-mount effects, i.e. after queries have already
 * fired. Gating here would turn every hard load into a network miss.
 */
export function peekContentCountsCache() {
  const data = readContentCountsValidated(false);
  // Miss attribution: without cookie consent, localStorage was never
  // consulted — labeling that miss "storage" would mislead the tracker.
  trackCacheRead("counts:organizations", data != null, data != null ? lastCountsReadSource : cookieConsent.allowed() ? "storage" : "memory");
  return data;
}

/** Capture the composite stamp BEFORE issuing the counts request so an
 *  entity-version bump landing mid-flight can't get stamped onto a response
 *  that was computed before it (see setPromotionsCache for the pattern). */
export function getContentCountsWriteStamp() {
  return currentContentCountsStamp();
}
export function setContentCountsCache(data, stamp) {
  if (typeof window === "undefined") return;
  const versions = stamp ?? currentContentCountsStamp();
  contentCountsMemory = {
    data,
    versions
  };
  trackCacheWrite("counts:organizations", Object.keys(data).length, cookieConsent.allowed() ? "storage" : "memory");
  if (!cookieConsent.allowed()) return;
  try {
    localStorage.setItem(CONTENT_COUNTS_KEY, JSON.stringify({
      data,
      sessionId: getCacheSessionId(),
      timestamp: Date.now(),
      versions
    }));
  } catch {}
}

// ── Promotions placement cache (version-gated) ───────────────────────────────
//
// Promotions have a server data version (bumped by every promotion mutation
// and propagated through /data-versions token + SSE) but no index page, so
// they never joined the index-cache pattern: every fresh page load re-fetched
// each placement. This cache serves a placement's rows for as long as
//   (a) the locally-known promotions version matches the write-time stamp
//       (currentKnownVersion reconciles the acknowledged key with the token
//       so a renewed token invalidates even on hook-less pages), and
//   (b) a 24h TTL backstop hasn't lapsed.
// Rows are mirrored VERBATIM from the server — including rows whose
// expiresAt has passed, because the BE list endpoint does not filter expiry
// either (findByPlacement filters isActive only); dropping them here would
// both change rendering and put the placement in a permanent miss/refetch
// loop. When the promotions version is locally unknown (expired token before
// renewal lands), reads MISS but leave the entry intact so the in-flight
// renewal can validate it on the next read.
//
// Writes go through a ticket captured BEFORE the network request
// (getPromotionsWriteTicket): the stamp reflects the version the response
// could have been computed under, and a generation counter lets
// clearPromotionsCache (called by admin promotion mutations at mutation
// start) reject in-flight writes that would re-seed pre-mutation rows.

const PROMOTIONS_CACHE_PREFIX = "pc_promotions_";
const PROMOTIONS_TTL_MS = 24 * 60 * 60 * 1000;
const promotionsMemory = new Map();
let promotionsCacheGeneration = 0;
/** Capture the version stamp + generation BEFORE issuing the network
 *  request; pass the ticket to setPromotionsCache with the response. */
export function getPromotionsWriteTicket() {
  return {
    version: currentKnownVersion("promotions"),
    generation: promotionsCacheGeneration
  };
}
export function getPromotionsCache(placement) {
  if (typeof window === "undefined") return null;
  const current = currentKnownVersion("promotions");
  const key = `promotions:${placement}`;
  const mem = promotionsMemory.get(placement);
  if (mem) {
    const ttlOk = Date.now() - mem.writtenAt < PROMOTIONS_TTL_MS;
    if (current !== null && mem.version === current && ttlOk) {
      trackCacheRead(key, true, "memory");
      return mem.data;
    }
    // Unknown current version (token mid-renewal): miss but keep the
    // entry — it may validate on the next read. Definite mismatch or
    // TTL lapse: drop it.
    if (current !== null || !ttlOk) promotionsMemory.delete(placement);
  }
  if (!cookieConsent.allowed()) {
    trackCacheRead(key, false, "memory");
    return null;
  }
  try {
    const raw = localStorage.getItem(`${PROMOTIONS_CACHE_PREFIX}${placement}`);
    if (!raw) {
      trackCacheRead(key, false, "storage");
      return null;
    }
    const parsed = JSON.parse(raw);
    const shapeOk = parsed && Array.isArray(parsed.data) && typeof parsed.version === "number" && typeof parsed.writtenAt === "number";
    const ttlOk = shapeOk && Date.now() - parsed.writtenAt < PROMOTIONS_TTL_MS;
    if (shapeOk && ttlOk && current !== null && parsed.version === current) {
      promotionsMemory.set(placement, {
        version: parsed.version,
        writtenAt: parsed.writtenAt,
        data: parsed.data
      });
      trackCacheRead(key, true, "storage");
      return parsed.data;
    }
    // Same grace rule as the memory path: only destroy entries that are
    // provably stale or malformed, not ones we merely can't verify yet.
    if (!shapeOk || !ttlOk || current !== null) {
      localStorage.removeItem(`${PROMOTIONS_CACHE_PREFIX}${placement}`);
    }
    trackCacheRead(key, false, "storage");
    return null;
  } catch {
    return null;
  }
}
export function setPromotionsCache(placement, data, ticket) {
  if (typeof window === "undefined") return;
  // A clearPromotionsCache since the fetch began means this response may
  // predate a mutation — never re-seed the cache with it.
  if (ticket && ticket.generation !== promotionsCacheGeneration) return;
  // Stamp with the pre-fetch version when known (the response cannot be
  // newer than the version the request was made under); fall back to the
  // write-time version for first-load flows where the token landed while
  // the fetch was in flight.
  const version = ticket?.version ?? currentKnownVersion("promotions");
  if (version === null) return;
  const writtenAt = Date.now();
  promotionsMemory.set(placement, {
    version,
    writtenAt,
    data
  });
  trackCacheWrite(`promotions:${placement}`, data.length, cookieConsent.allowed() ? "storage" : "memory");
  if (!cookieConsent.allowed()) return;
  try {
    localStorage.setItem(`${PROMOTIONS_CACHE_PREFIX}${placement}`, JSON.stringify({
      data,
      version,
      writtenAt
    }));
  } catch {}
}

/** Wipe every placement's cached rows (memory + localStorage) and bump the
 *  write generation so in-flight fetches can't re-seed pre-clear rows.
 *  Called by admin promotion mutations at mutation start. */
export function clearPromotionsCache() {
  promotionsCacheGeneration += 1;
  promotionsMemory.clear();
  if (typeof window === "undefined") return;
  try {
    const doomed = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PROMOTIONS_CACHE_PREFIX)) doomed.push(k);
    }
    doomed.forEach(k => localStorage.removeItem(k));
  } catch {}
}

// ── Per-user presence memos ──────────────────────────────────────────────────
//
// Some authenticated queries exist only to answer "does this user have any
// X?" for the overwhelmingly common case of "no" (event submissions, a
// professional profile). A settled answer is memoised per user so index
// pages can skip the fetch entirely; the memo is cleared by the user's own
// mutations and expires after 7 days so cross-device drift self-heals.

const USER_MEMO_PREFIX = "pc_user_memo_";
const USER_MEMO_TTL_MS = 7 * 24 * 60 * 60 * 1000;
export function getUserPresenceMemo(kind, userId) {
  if (typeof window === "undefined") return null;
  if (!cookieConsent.allowed()) return null;
  try {
    const raw = localStorage.getItem(`${USER_MEMO_PREFIX}${kind}_${userId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.has !== "boolean" || typeof parsed.at !== "number") {
      return null;
    }
    if (Date.now() - parsed.at > USER_MEMO_TTL_MS) {
      localStorage.removeItem(`${USER_MEMO_PREFIX}${kind}_${userId}`);
      return null;
    }
    return parsed.has;
  } catch {
    return null;
  }
}
export function setUserPresenceMemo(kind, userId, has) {
  if (typeof window === "undefined") return;
  if (!cookieConsent.allowed()) return;
  try {
    localStorage.setItem(`${USER_MEMO_PREFIX}${kind}_${userId}`, JSON.stringify({
      has,
      at: Date.now()
    }));
  } catch {}
}

/** Drop every user's memo of one kind (mutations don't always know the
 *  user id — clearing the kind wholesale is cheap and always correct). */
export function clearUserPresenceMemo(kind) {
  if (typeof window === "undefined") return;
  try {
    const prefix = `${USER_MEMO_PREFIX}${kind}_`;
    const doomed = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefix)) doomed.push(k);
    }
    doomed.forEach(k => localStorage.removeItem(k));
  } catch {}
}

// ── Detail page data cache ───────────────────────────────────────────────────

const DETAIL_CACHE_PREFIX = "pc_detail_cache_";
const detailMemoryCache = new Map();
// Parallel map storing the data-version stamp + payload reference for each
// cached entry so the in-memory fast path can detect server bumps without
// re-parsing localStorage.
const detailMemoryCacheMeta = new Map();
function detailCacheKey(type, slug) {
  return `${type}:${slug}`;
}

/**
 * Get cached detail page data.
 * Returns from in-memory map instantly (client navigations).
 * Falls back to localStorage with the same freshness checks as index cache.
 */
let lastDetailReadSource = "storage";

/** Tracking wrapper — see {@link getDetailCacheInner} for the real logic. */
export function getDetailCache(type, slug) {
  const data = getDetailCacheInner(type, slug);
  trackCacheRead(`detail:${type}:${slug}`, data != null, data != null ? lastDetailReadSource : "storage");
  return data;
}
function getDetailCacheInner(type, slug) {
  if (typeof window === "undefined" || !slug) return null;
  const key = detailCacheKey(type, slug);

  // Fast path: in-memory hit (only honoured when our last-known data
  // version still matches the server-bumped one — otherwise we let the
  // localStorage path below fall through to the version-stamp check).
  const indexType = detailTypeToIndexType(type);
  const currentVersion = indexType ? getCurrentDataVersion(indexType) : null;
  const memEntry = detailMemoryCacheMeta.get(key);
  if (memEntry) {
    if (currentVersion === null || memEntry.version === null || memEntry.version >= currentVersion) {
      lastDetailReadSource = "memory";
      return memEntry.data;
    }
    // Memory copy predates a server bump — drop it and fall through.
    detailMemoryCache.delete(key);
    detailMemoryCacheMeta.delete(key);
  }

  // Before hydration completes, never read from localStorage
  if (!hydrated) return null;
  if (!cookieConsent.allowed()) return null;
  try {
    const raw = localStorage.getItem(`${DETAIL_CACHE_PREFIX}${key}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.data) return null;

    // Version freshness: if the server has bumped the data version since
    // this entry was written, treat as stale so the page falls back to
    // the live RTK Query result instead of flashing pre-edit content.
    if (indexType) {
      const stampedVersion = typeof parsed.dataVersion === "number" ? parsed.dataVersion : null;
      if (currentVersion !== null && stampedVersion !== null && stampedVersion < currentVersion) {
        localStorage.removeItem(`${DETAIL_CACHE_PREFIX}${key}`);
        return null;
      }
    }

    // Freshness check (same rules as index cache)
    const hasToken = !!localStorage.getItem("pc_auth_token");
    if (hasToken) {
      const currentSession = getCacheSessionId();
      if (!parsed.sessionId || parsed.sessionId !== currentSession) {
        localStorage.removeItem(`${DETAIL_CACHE_PREFIX}${key}`);
        return null;
      }
    } else {
      const age = Date.now() - (parsed.timestamp ?? 0);
      if (age > STALE_MS) {
        localStorage.removeItem(`${DETAIL_CACHE_PREFIX}${key}`);
        return null;
      }
    }
    detailMemoryCache.set(key, parsed.data);
    detailMemoryCacheMeta.set(key, {
      data: parsed.data,
      version: typeof parsed.dataVersion === "number" ? parsed.dataVersion : null
    });
    lastDetailReadSource = "storage";
    return parsed.data;
  } catch {
    return null;
  }
}

/** Cache detail page data with session ID and timestamp. */
export function setDetailCache(type, slug, data) {
  if (typeof window === "undefined" || !slug) return;
  const key = detailCacheKey(type, slug);
  const indexType = detailTypeToIndexType(type);
  const version = indexType ? getCurrentDataVersion(indexType) : null;
  detailMemoryCache.set(key, data);
  detailMemoryCacheMeta.set(key, {
    data,
    version
  });
  trackCacheWrite(`detail:${type}:${slug}`, undefined, cookieConsent.allowed() ? "storage" : "memory");
  if (!cookieConsent.allowed()) return;
  try {
    localStorage.setItem(`${DETAIL_CACHE_PREFIX}${key}`, JSON.stringify({
      data,
      sessionId: getCacheSessionId(),
      timestamp: Date.now(),
      dataVersion: version
    }));
  } catch {
    // quota errors — ignore
  }
}

/**
 * Remove all detail-page caches for a given entity type (or all types).
 * Clears both the in-memory map and matching localStorage entries.
 */
export function clearDetailCaches(type) {
  if (typeof window === "undefined") return;
  const types = type ? [type] : ["regulation", "article", "event", "organization"];
  // Purge memory map
  for (const key of Array.from(detailMemoryCache.keys())) {
    const [t] = key.split(":");
    if (types.includes(t)) {
      detailMemoryCache.delete(key);
      detailMemoryCacheMeta.delete(key);
    }
  }
  // Purge localStorage
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(DETAIL_CACHE_PREFIX)) continue;
      const suffix = k.slice(DETAIL_CACHE_PREFIX.length);
      const [t] = suffix.split(":");
      if (types.includes(t)) toRemove.push(k);
    }
    toRemove.forEach(k => localStorage.removeItem(k));
  } catch {}
}